import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { theme } from 'antd';
import { XProvider } from '@ant-design/x';
import dayjs from 'dayjs';
import locale from 'antd/locale/zh_CN';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/zh-cn';
import { IsLiteProvider, AppShell, AbortControllerProvider } from '@/context';
import * as themeTokens from './theme-tokens';
import { sizeToken } from './size-tokens';
import Routes from './routes';
import { basePath } from './common/base-route-url';
import './App.less';

dayjs.locale('zh-cn');
dayjs.extend(utc);
dayjs.extend(timezone);

const { defaultAlgorithm, getDesignToken, darkAlgorithm } = theme;
const { lightToken, darkToken, ...customThemeTokens } = themeTokens;

const isIframe = window.self !== window.top;
const isNotPC =
    isIframe ||
    /Mobi|Android/i.test(window.navigator.userAgent) ||
    /Tablet|iPad/i.test(window.navigator.userAgent);
const minScreen = 768;


// nonce 严格判断，防止传递 'undefined' 字符串
const nonce = (typeof window !== 'undefined' && typeof window.__CSP_NONCE__ === 'string' && window.__CSP_NONCE__)
  ? window.__CSP_NONCE__
  : undefined;

const camelToKebab = (str) => {
    return str.replace(/([A-Z])/g, (match) => `-${match.toLowerCase()}`);
};

function App() {
    const curTheme = useSelector((state: any) => state.theme);

    const [ isLiteLayout, setIsLiteLayout ] = useState(
        isNotPC || window.matchMedia(`(max-width: ${minScreen}px)`).matches
    );
    const [ isMobile, setIsMobile ] = useState(isNotPC);

    const themeToken = useMemo(() => {
        const customTokenName = Object.keys(customThemeTokens).find(themeName => (
            camelToKebab(themeName.split('Token')[0]) === curTheme
        ));
        const customToken = customTokenName ? customThemeTokens[customTokenName] : {};

        const lightConfig = {
            token: {
                ...lightToken,
                ...customToken
            }
        };
        const darkConfig = {
            token: {
                ...darkToken,
                ...customToken
            }
        };

        const lightGlobalToken = getDesignToken(lightConfig);
        const darkGlobalToken = getDesignToken(darkConfig);

        const mapV5LightToken = defaultAlgorithm(lightGlobalToken);
        const mapV5DarkToken = darkAlgorithm(darkGlobalToken);

        if (`${curTheme}`.startsWith('dark')) {
            return {
                ...mapV5DarkToken,
                ...darkGlobalToken
            };
        }

        return {
            ...mapV5LightToken,
            ...lightGlobalToken
        };
    }, [ curTheme, customThemeTokens ]);

    useEffect(() => {
        const handleResize = (e) => {
            const curWidth = e.currentTarget.innerWidth;
            setIsMobile('ontouchstart' in window || navigator.maxTouchPoints > 0);
            setIsLiteLayout(curWidth < minScreen);
        };

        window.addEventListener('resize', handleResize);
        document.body.classList.add('loaded');

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        document.body.classList.forEach((className) => {
            if (className.endsWith('body')) {
                document.body.classList.remove(className);
            }
        });

        document.body.classList.add(`${curTheme}-body`);
    }, [ curTheme ]);

    return (
        <XProvider
            csp={nonce ? { nonce } : undefined}
            locale={locale}
            theme={{
                token: {
                    ...themeToken,
                    ...sizeToken
                },
                components: {}
            }}
        >
            <IsLiteProvider
                value={{
                    isLiteLayout,
                    isMobile
                }}
            >
                <AppShell>
                    <AbortControllerProvider>
                        <div className="App">
                            <Router basename={basePath}>
                                <Routes />
                            </Router>
                        </div>
                    </AbortControllerProvider>
                </AppShell>
            </IsLiteProvider>
        </XProvider>
    );
}

export default App;
